import { useState } from "react";
import { ChevronDown, ChevronRight, ExternalLink, Globe } from "lucide-react";

interface SourceItem {
  url: string;
  title: string;
  snippet?: string;
  description?: string;
  favicon?: string;
}

interface SourcesBlockProps {
  sources: SourceItem[];
}

function getFaviconUrl(url: string): string {
  try {
    const host = new URL(url).hostname;
    return `https://www.google.com/s2/favicons?domain=${host}&sz=16`;
  } catch {
    return "";
  }
}

export function SourcesBlock({ sources }: SourcesBlockProps) {
  const [isOpen, setIsOpen] = useState(false);

  if (sources.length === 0) return null;

  return (
    <div className="my-2 rounded-lg border border-border">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-accent/50 transition-colors"
        onClick={() => setIsOpen((o) => !o)}
        aria-expanded={isOpen}
      >
        {isOpen ? (
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
        )}
        <Globe className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="font-medium">
          {sources.length} source{sources.length > 1 ? "s" : ""}
        </span>
      </button>

      {isOpen && (
        <div className="border-t divide-y divide-border">
          {sources.map((source, i) => (
            <a
              key={`${source.url}-${i}`}
              href={source.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-3 px-3 py-2 hover:bg-accent/50 transition-colors group"
            >
              <img
                src={source.favicon || getFaviconUrl(source.url)}
                alt=""
                className="mt-0.5 h-4 w-4 rounded-sm"
                loading="lazy"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1 text-sm font-medium text-primary group-hover:underline">
                  <span className="truncate">{source.title}</span>
                  <ExternalLink className="h-3 w-3 shrink-0 opacity-0 group-hover:opacity-100" />
                </div>
                {(source.description || source.snippet) && (
                  <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">
                    {source.description || source.snippet}
                  </p>
                )}
                <p className="mt-0.5 text-[10px] text-muted-foreground/70 truncate">
                  {source.url}
                </p>
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
